// Non-Haven Imports
import { Request, Response } from 'express';

import _ from 'lodash';
import * as Url from 'url';
import safeJsonStringify from 'safe-json-stringify';
import jwt from 'jsonwebtoken';

// Haven Module Imports
import { supertypeClass, property, remote, amorphicStatic, Supertype, IAmorphicAppController } from '@haventech/amorphic';
import { CallContext, PreServerCallChanges } from '@haventech/amorphic/dist/lib/types/HelperTypes';
import { HTTPObjs, RemoteCall } from '@haventech/amorphic/dist/lib/types/ILifecycleController';
import {MetadataCategory, MetadataApi} from '@hit/metadata-module';
import {CategoryMetaMap} from '@hit/metadata-module/dist/lib/Types';

// Internal Imports
import {PrincipalCustomer} from '../../../common/js/PrincipalCustomer';
import {ProductRulesController} from './../../../common/js/ProductRulesController';
import {Product} from './../../../common/js/static/Product';
import {Emailer} from './../../../common/js/services/emailer/Emailer';
import {EmailParams} from '../../../common/js/interfaces/EmailParams';
import {MMDirectControllerInterface} from '../../../coverpath-common/angular/interfaces/controller/MMDirectControllerInterface';
import {
    PremiumRequest,
    PremiumRequestList,
    PremiumResponse,
    PremiumResponseList,
    ProductServiceClient,
    ProductSpecRequest,
    ProductSpecResponse
} from '../../../common/js/productService/ProductServiceClient';
import {Utils} from './../../../common/js/Utils';
import {AuthOwner, Customer} from '../../../common/js/customer/Customer';
import {Policy} from './../../../common/js/customer/Policy';
import {ExistingPolicies} from '../../../common/js/customer/ExistingPolicies';
import {Address} from '../../../common/js/customer/Address';
import {Entity} from './../../../common/js/customer/Entity';
import {Financials} from '../../../common/js/customer/Financials';
import {Trustee} from '../../../common/js/customer/Trustee';
import {Grantor} from '../../../common/js/customer/Grantor';
import {Signer} from '../../../common/js/customer/Signer';
import {AMCSSOPayload, FeatureFlags} from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import {LoginType} from '../../../coverpath-common/angular/classes/LoginType';
import {WorkflowHelper} from '../../../coverpath-common/classes/WorkflowHelper';
import {PolicyAdminStatus} from './../../../common/js/policyAdmin/PolicyAdminStatus';
import { AuthenticatedPrincipal } from "../../../common/js/AuthenticatedPrincipal";
import { ProductQuoteResponse, ProductQuoteResponseList, ClientCustomerProps } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { ApplicationController } from '../../../coverpath-common/angular/amorphicControllers/ApplicationController';
import { AccountCenterController } from './amorphicControllers/AccountCenterController';
import { PolicyAdminUtils } from './../../../common/js/policyAdmin/PolicyAdminUtils';
import { HavenAnalytics } from '../../../common/js/HavenAnalytics';
import { CoverpathUtils } from '../../../coverpath-common/js/static/CoverpathUtils';

import { SubmittedPolicyValidatingController } from '../../../common/js/controller/SubmittedPolicyValidatingController';
import {PolicyAdminClient} from '../../../common/js/policyAdmin/PolicyAdminClient';
import {RemoteDocServiceController} from '../../../common/js/controller/RemoteDocServiceController';
import {Auth0Channel, Auth0Configs, Auth0Utils} from '../../../common/js/authentication/Auth0Utils';
import {AppConfigTypes} from '../../../common/js/config/AppConfig';
import {
    AuthenticationInformation,
    IdentifiedPrincipal,
    Auth0Controller
} from '../../../common/js/authentication/Auth0Controller';
import {Identity} from '../../../common/js/account/identity/Identity';
import {Auth0Client, Auth0Response, AMCAuth0ResponseUser} from '../../../common/js/authentication/Auth0Client';
import {LaunchDarklyInstance, LD_APP} from '../../../common/js/launch-darkly/LaunchDarklySingleton';
import {AMCController} from '../../../common/js/authentication/amc/AMCController';
import {Account} from '../../../common/js/account/Account';
import {AccountAuthorization} from '../../../common/js/account/authorization/AccountAuthorization';
import * as AuthenticationHelpers from '../../../common/js/AuthenticationHelpers';
import {ApplicationDataService} from '../../../coverpath-common/js/static/ApplicationDataService';
import {HitAppCtx} from '../../../common/js/orca/HitAppCtx';
import {Person} from '../../../common/js/customer/Person';
import {CustomerUtils} from '../../../common/js/customer/CustomerUtils';

import '../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectCustomer';
import '../../../coverpath-common/classes/lifebridge_subclasses/LifeBridgeCustomer';
import { MMDirectCustomer } from '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectCustomer';
import { LifeBridgeCustomer } from '../../../coverpath-common/classes/lifebridge_subclasses/LifeBridgeCustomer';
import { MMDirectPolicy } from '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectPolicy';
import { serverOnly } from '../../../common/js/helpers';
import { MMDirectApplication } from '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectApplication';
import { GiactController } from "./../../../coverpath-common/js/controllers/GiactController";
import { CommonUtilsBackend } from '../../../common/js/static/CommonUtilsBackend';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';
import { UwClientAppCtx } from '@hit/underwriting-api-client';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../common/js/logger/UILoggerSingleton';

amorphicStatic['toClientRuleSet'] = ['mm-direct'];
declare let window;

@supertypeClass({toClient: true, toServer: true})
export class Controller extends Auth0Controller implements MMDirectControllerInterface, IAmorphicAppController {

    @property()
    customer: MMDirectCustomer;

    @property({toClient: false, getType: () => Emailer})
    emailer: Emailer;

    @property({toServer: false})
    nonProdMode: boolean;

    @property({ getType: () => PolicyAdminStatus })
    policyAdminStatus: PolicyAdminStatus = null;

    @property()
    accountCenterController: AccountCenterController;

    @property()
    productRulesController: ProductRulesController;

    @property()
    applicationController: ApplicationController;

    @property({ getType: () => GiactController })
    giactController: GiactController;

    @property()
    amcController: AMCController;

    @property()
    workflowHelper: WorkflowHelper;

    @property({toClient: false, rule:["required", "password"], sensitiveData: true})
    newPassword: string = '';

    @property({toClient: false, rule:["required"], sensitiveData: true})
    password: string = '';

    @property({toClient: false, getType: () => SubmittedPolicyValidatingController})
    submittedPolicyValidatingController: SubmittedPolicyValidatingController;

    @property()
    remoteDocServiceController: RemoteDocServiceController;

    @property()
    isLifeBridge: boolean = false;

    @property({ toServer: false })
    artEnabled: boolean = false;

    @property({ toClient: true, toServer: false })
    isHeavyUserEnabled: boolean = false;

    @property({ toServer: false })
    earlyInsuredConsentEnabled: boolean = false;

    @property({ toServer: false })
    isCoverpathClient: boolean = false;

    @property({ toServer: false })
    mmGiactWebDebitVerificationServiceEnabled = {};

    @property({ toServer: false })
    enableGenesysCobrowse = false;

    getPrincipal(): AuthenticatedPrincipal {
        return this.customer;
    }

    setPrincipal(principal): void {
        this.customer = principal;
    }

    newPrincipal(): MMDirectCustomer {
      if (this.isLifeBridge) {
        return LifeBridgeCustomer.new('mmDirectQuote');
      } else {
        return MMDirectCustomer.new('mmDirectQuote');
      }

    }

    sendEmail(template: string, email: string, name: string, vars): void {
        const params: EmailParams = _.reduce(vars, (obj, elem) => {
            obj[elem.name] = elem.content;
            return obj;
        }, {});

        const slugs = {
            register_verify: 'notify-registration-welcome',
            email_changed: 'notify-old-email-changed',
            email_changed_verify: 'verify-email-changed',
            password_reset: 'notify-password-reset',
            password_changed: 'confirmation-password-changed',
            confirm_emailchange: 'confirmation-email-changed',
            email_verify: 'notify-validation-code'
        };

        this.emailer.sendEmail({
            slug: slugs[template],
            channel: Product.channels.MMDirect.code,
            recipientEmail: email,
            recipientName: name,
            confirmLink: params.link,
            confirmPin: params.verificationCode,
            resetLink: params.link
        });
    }

    refresh() {}

    reset(): void {}

    clientInit() {
        if (this.customer) {
            this.customer.clientInit();
        }
        else {
            this.customer = this.newPrincipal();
        }

        if (!this.productRulesController) {
            this.productRulesController = new ProductRulesController();
        }
        this.productRulesController.loadRules(Product.channels.MMDirect.code);

        if (!this.workflowHelper) {
            this.workflowHelper = WorkflowHelper.new();
        }

        if (!this.accountCenterController) {
            this.accountCenterController = AccountCenterController.new(this);
        }

        if (!this.applicationController) {
            this.applicationController = ApplicationController.new();
        }

        if (!this.giactController) {
            this.giactController = GiactController.new();
        }

        setInterval(() => {
            if(window.amorphic.state === 'zombie') {
                document.location.href = '/inactive_tab.html';
                UILoggerSingleton.getInstance().info(
                    {
                      module: 'controller',
                      function: 'clientInit',
                      category: UICategory.request,
                    },
                     `Tab entered zombie mode, redirecting to /inactive_tab.html`
                );
            }
        }, 100);
    }

    serverInit(): void {
        super.serverInit();
        // set UwAppCtx global logger so we don't error out
        try {
            const ctx = HitAppCtx.get;
            UwClientAppCtx.get.makeGlobal(LoggerSingleton.getInstance());
        } catch (e) {
            const err = {
                isHumanRelated: false,
                message: e?.message
            };
            LoggerSingleton.getInstance().error({
                module: 'Controller:mm-direct',
                function: 'serverInit',
                category: Category.availability,
                message: 'UwClientAppCtx.get.makeGlobal error',
                error: err
            });
        }

        const environment = this.amorphic.config.nconf.get('environment');
        this.nonProdMode = !Utils.isProdEnv();

        this.emailer = this.emailer || HitAppCtx.get.emailer;

        this.submittedPolicyValidatingController =
            this.submittedPolicyValidatingController || SubmittedPolicyValidatingController.new();

        if (!this.remoteDocServiceController) {
            this.remoteDocServiceController = RemoteDocServiceController.new(this);
        }
    }

    handleRemoteError(error) {
        // Handle framework errors only
        if (error && error.code && error.code.match(/internal_error/)) {
            window.location.reload(true);
        }
    }

    /**
     * When the app container restarts, Amorphic resets the active session on the next remote call but the session
     * doesn't flow to the client causing semotus sync errors. Side effect is remote calls fail leading
     * to cases like - user clicking on the clients tab sees an eternal spinner or user filling an application
     * thinks that changes are persisted but they are not.
     * Amorphic provides the following callback function to let controllers do specific processing on
     * a reset, here we merely reload the page to get the session onto the client.
     */
    shutdown(): void {
        window.location.reload();
    }

    async processPost(
        originalUrl: string,
        body: { path?: string },
        req: Request,
        res: Response
    ): Promise<{ status: number; body?: string; headers: { location: string, 'Content-Type': string } }> {
        if (body && body.path) {
            UILoggerSingleton.getInstance().info(
                {
                  module: 'Controller',
                  function: 'processPost',
                  category: UICategory.request,
                  data: {
                    path: body.path
                  }
                },
                `Error Controller`
            );

            if (body.path === '/fetchSession') {
                return this.fetchAMCAuthData();
            }
            else {
                return { status: 400, headers: { 'Content-Type': 'application/json', 'location': '' } };
            }
        }
    }

    fetchAMCAuthData() {
        const data: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.MMD);

        UILoggerSingleton.getInstance().info(
            {
                module: 'Controller',
                function: 'fetchAMCAuthData',
                category: UICategory.security,
            },
             'Fetching data from configs for AMC silent authentication'
        );

        const stringifiedConfig = JSON.stringify(data);

        return { status: 200, headers: { 'Content-Type': 'application/json', 'location': '' }, body: stringifiedConfig };
    }

    getAMCController(): AMCController {
        if (!this.amcController) {
            this.amcController = AMCController.new(this);
        }
        return this.amcController;
    }

    @remote({ on: "server" })
    async publicFetchMetaData() {
        try {
            await this.setDataForLocalProps();
        }
        catch (err) {
            const error = {
                isHumanRelated: false,
                message: err
            }
            LoggerSingleton.getInstance().error({
                module: 'controller',
                function: 'publicFetchMetaData',
                category: Category.request,
                error: error,
            },
            `Error in call to getDataForLocalProps`
            );
        }

        try {
            return this.getMetadataForPolicy(this.customer.application.primaryPolicy);
        }
        catch(err){
            const error = {
                isHumanRelated: false,
                message: err
            }
            LoggerSingleton.getInstance().error({
               module: 'controller',
               function: 'publicFetchMetaData',
               error: error,
               category: Category.request,
             },
              "Error in call to getMetadataForPolicy"
            );
        }
    }

    async setDataForLocalProps() {
        const primaryPolicy = this.customer.application.primaryPolicy;
        const secondaryPolicy = this.customer.application.secondaryPolicy;

        if (primaryPolicy) {
            await ApplicationDataService.updatePolicyLocalProps(this, primaryPolicy);
        }
        if (secondaryPolicy) {
            await ApplicationDataService.updatePolicyLocalProps(this, secondaryPolicy);
        }
    }

    async getMetadataForPolicy(policy: Policy): Promise<CategoryMetaMap> {
        //if versionForPolicy is defined, we'll get the Metadata for that specific version
        //if it's undefined, we'll get the latest version

        const version = policy.appMetadataVersion?.toString() || undefined;
        const category = new MetadataCategory('App_Questions', version);

        // Force hierarchy of MMDirect policy to be CAS for application construction since they share metadata
        const hierarchy = policy.getApplicationHierarchy();
        const result = await MetadataApi.constructMetadata(category, hierarchy, null);

        if(policy.appMetadataVersion === null || isNaN(policy.appMetadataVersion)) {
            UILoggerSingleton.getInstance().info({
                module: 'controller',
                function: 'getMetadataForPolicy',
                category: UICategory.security,
             },
              `Stamping ${policy._id} with metadata version of ${result.version}`
            );

            policy.appMetadataVersion = parseFloat(result.version);
        }

        return result.metadata;
    }

    @remote({ on: "server" })
    publicGetLocationByZip(zipcode) {
        return CommonUtilsBackend.publicGetLocationByZip(zipcode);
    }

    @remote({ on: "server" })
    publicSave(): Promise<void> {
        return Promise.resolve();
    }

    @remote({ on: 'server'})
    public async publicFetchMultipleProductQuotes(
        requestOptions: PremiumRequestList,
        productPremiumVersion?: string
    ): Promise<ProductQuoteResponseList> {
        LoggerSingleton.getInstance().info({
            module: 'controller.publicFetchMultipleProductQuotes',
            function: 'publicFetchMultipleProductQuotes',
            category: Category.security,
            data: {
                productServicePayload: safeJsonStringify(requestOptions)
            },
         },
          'fetching a quote from the product service'
        );

        // don't have a policy number for a quote, use customer ID instead to track req through system.
        const premiumList: PremiumResponseList = await ProductServiceClient.getMultiplePremiums(this.customer._id, productPremiumVersion, requestOptions);

        const productQuoteReponseList: ProductQuoteResponseList = {
            responses: []
        };

        premiumList.responses.forEach((premium, i) => {
            const productQuoteReponse: ProductQuoteResponse = {
                base: Utils.round(Number(premium.base), 2).valueOf(),
                waiver: requestOptions.requests[i].waiver ? Utils.round(Number(premium.waiver), 2).valueOf() : 0,
                riders: premium.riders || [],
                flatExtras: premium.flatExtras || [],
                annualBaseRates: premium.annualBaseRates || [],
                annualWaiverRates: premium.annualWaiverRates || [],
                totalTargetPremium: premium.totalTargetPremium,
                annualBase: Utils.round(Number(premium.annualBase), 2).valueOf()
            };

            productQuoteReponseList.responses.push(productQuoteReponse);
        });

        return productQuoteReponseList;
    }

    @remote({ on: 'server' })
    public async publicFetchProductQuote(requestOptions: PremiumRequest, requestIdentifier?: string): Promise<ProductQuoteResponse> {
        LoggerSingleton.getInstance().info({
            module: 'controller',
            function: 'publicFetchProductQuote',
            data: {
                productServicePayload: safeJsonStringify(requestOptions)
            },
            category: Category.request,
         },
          'fetching a quote from the product service'
        );

        // don't have a policy number for a quote, use customer ID instead to track req through system.
        const premium: PremiumResponse = await ProductServiceClient.getPremium(requestIdentifier || this.customer._id, null, requestOptions);

        return {
            base: premium.base,
            waiver: requestOptions.waiver ? premium.waiver : 0
        };
    }

    @remote({ on: 'server' })
    public async publicFetchProductSpec(requestOptions: ProductSpecRequest): Promise<ProductSpecResponse> {
        LoggerSingleton.getInstance().info({
            module: 'controller',
            function: 'publicFetchProductSpec',
            data: {
                productServicePayload: safeJsonStringify(requestOptions)
            },
            category: Category.request,
         },
          'fetching a product spec from the product service'
        );

        const productSpec: ProductSpecResponse = await ProductServiceClient.getProductSpec(requestOptions);
        return productSpec;
    }

    @remote({on: "server"})
    async register(url: string): Promise<boolean> {
      this.isLifeBridge = (/lifebridge/).test(url.toLowerCase());
      const authInfo = await this.publicControllerAuthenticate();
      await this.prepareSessionData(authInfo);
      return true;
    }

    @remote({on: "server"})


  protected async identifyPrincipal(email: string, firstName: string, lastName: string): Promise<IdentifiedPrincipal> {
    let isExistingCustomerObject: boolean = true;

    const customerAccount: Account = await this.fetchCustomerAccount();

    if (customerAccount && this.isAuth0Enabled) {
      const authorizedAccount: AccountAuthorization = customerAccount.accountAuthorizations.find(accountAuth => {
        return accountAuth.managedEntityType.templateName === MMDirectCustomer.amorphicClassName
            || accountAuth.managedEntityType.templateName === LifeBridgeCustomer.amorphicClassName;
      });

      let customer: PrincipalCustomer;
      if (authorizedAccount) {
        customer =  await Customer.persistorFetchById(authorizedAccount.managedEntityId);
      } else {
        customer = await this.createUser(email, firstName, lastName);
        isExistingCustomerObject = false;
      }

      return {
        principal: customer,
        isExistingUser: isExistingCustomerObject
      };
    }
    // -----------------------------------------
    else {
      const principals = await this.fetchPrincipalQuery(email);
      let principal;

      if (principals.length === 0) {
        // None found -> Create a new account
        principal = await this.createUser(email, firstName, lastName);
        isExistingCustomerObject = false;
      } else {
        // Match found -> Use existing account
        principal = principals[0];
        isExistingCustomerObject = true;
      }

      // Suspended or Non-Existent
      if (!principal) {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'Auth0Controller',
            function: 'identifyPrincipal',
            context: {
               piiLevel: UIPiiLevelEnum.s2
            },
            data: {
                email: email,
                hasPrincipal: !!principal
             },
            category: UICategory.security,
         },
          'Log In attempt failed.  Unable to identify user'
        );

        throw {
          code: 'invalid_user',
          text: 'Incorrect email or password'
        };
      }

      if (principal.suspended) {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'Auth0Controller',
            function: 'identifyPrincipal',
            context: {
                piiLevel: UIPiiLevelEnum.s2
            },
            data: {
                email: email,
                isSuspended: principal.suspended
            },
            category: UICategory.security,
         },
          'Log In attempt failed (user suspended)'
        );

        throw {
          code: 'invalid_user_suspended',
          text: 'User Suspended'
        };
      }

      UILoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'identifyPrincipal',
            context: {
                piiLevel: UIPiiLevelEnum.s2
            },
            data: {
                identifiedPrincipal: principal.email,
                authOwner: principal.authOwner,
                isExistingUser: isExistingCustomerObject
            },
            category: UICategory.security,
         },
          'Identified principal'
      );

      return {
        principal: principal,
        isExistingUser: isExistingCustomerObject
      };
    }
  }

protected async createUser(email: string, firstName: string, lastName: string): Promise<PrincipalCustomer> {
    let principal = this.getPrincipal();
    if (!principal || this.isLifeBridge) {
        principal = this.newPrincipal();
        this.setPrincipal(principal);
    }
    principal.email = email;
    principal.firstName = firstName;
    principal.lastName = lastName;

    if (!this.isAuth0Enabled) {
      this.customer.previousSalts = [];
      this.customer.previousHashes = [];

      await this.customer.setEmailVerificationCode();
      await this.customer.establishPassword(this.newPassword, null, true, false);
    }

    await AuthenticationHelpers.insertFilter.call(this, principal);
    await principal.persistSave();

    return principal;
  }

    async getCustomerStatusForEmail(email: string): Promise<string> {
        if (!email) {
            return "nonexistent";
        }

        try {
            const results = await MMDirectCustomer.persistorFetchByQuery({email: email});

            if (results && results[0] && results[0].passwordHash) {
                return "exists:registered";
            }
            else if (results && results[0]) {
                return "exists:notregistered";
            }
            else {
                return "nonexistent";
            }
        }
        catch(error) {
            UILoggerSingleton.getInstance().info({
                module: 'Controller',
                function: 'getCustomerStatusForEmail',
                category: UICategory.request,
             },
              error
            );
            throw(error);
        }
    }

    makePropsForNewCustomer(): ClientCustomerProps {
        const policy = {
            monthly: this.customer.mmdQuote.premium,
            waiverOfPremium: this.customer.mmdQuote.waiverOfPremiumAmount,
            carrier: 'MassMutual',
            face: this.customer.mmdQuote.faceAmount,
            term: this.customer.mmdQuote.term,
            paymentFrequency: this.customer.mmdQuote.paymentFrequency,
            product: this.customer.mmdQuote.product,
            hasEcp: this.customer.mmdQuote.product === Product.products.MMDirectTermECP.code ? true : false,
            carrierCode: 'CAS',
            rating: 'A++'
        };
        const quote = {
            product: this.customer.mmdQuote.product ? this.customer.mmdQuote.product : 'MMDirectTerm',
            productType: Product.productTypes.Term.code,
            faceAmount: this.customer.mmdQuote.faceAmount || 1e5,
            term: this.customer.mmdQuote.term || 20,
            paymentFrequency: this.customer.mmdQuote.paymentFrequency,
            smoker: this.customer.mmdQuote.smoker || false,
            dateOfBirth: this.customer.mmdQuote.dateOfBirth || new Date('January 1, 1983'),
            premium: this.customer.mmdQuote.premium || 0,
            healthClass: this.customer.mmdQuote.healthClass || 'ULTRA',
            contractState: this.customer.mmdQuote.contractState || 'MA',
            waiverOfPremium: this.customer.mmdQuote.waiverOfPremium || false,
            gender: this.customer.mmdQuote.gender || 'male',
            waiverOfPremiumAmount: this.customer.mmdQuote.waiverOfPremiumAmount || 0,
            policies: [policy],
            premiumWithECP: this.customer.mmdQuote.premiumWithECP,
            premiumWithoutECP: this.customer.mmdQuote.premiumWithoutECP
        };

        const personAge = quote.dateOfBirth ? Utils.getAgeFromBirthday(quote.dateOfBirth.toLocaleDateString()) : null;
        return {
            quote: quote,
            quoteType: 'mmDirectQuote',
            person: {
                email: this.email,
                firstName: this.firstName,
                lastName: this.lastName,
                age: personAge
            },
            address: {
                state: this.customer.mmdQuote.contractState
            },
            progress: 1,
            primaryQuote: this.customer.mmdQuote
        };
    }

    @remote({on: "server"})
    login(loginType?: LoginType): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.loginByType(loginType)
            .then(() => this.fetchAccessories())
            .then(() => this.postProcessing(loginType))
            .then(() => {
                this.loggedIn = true;
                this.customer.clientInit();
                this.clearPasswords();
                this.customer.advanceApplicationProgress(12);

                resolve(true);
            }).catch((error) => {
                reject(false);
            });
        });
    }

    @remote({on: "server"})
    async resetPassword(url: string): Promise<void> {
        if (this.isAuth0Enabled) {
            await this.getAMCController().updateAMCPassword();
            this.clearPasswords();
        } else {
            return this.publicResetPassword(url, this.customer.channel);
        }
    }

    @remote({on: "server"})
    async resetEmail(url: string): Promise<void> {
        if (this.isAuth0Enabled) {
            const customerExists = await this.checkIfCustomerExistsWithEmail(this.newEmail);
            if (customerExists) {
              throw { code: 'email_registered', text: 'This email is already registered' };
            } else {
              await this.getAMCController().updateAMCEmail();
            }
        } else {
            await this.changeEmail(undefined, url);
            this.clearPasswords();
        }
    }

    private async checkIfCustomerExistsWithEmail(email: string): Promise<boolean> {
        UILoggerSingleton.getInstance().info({
            module: `Controller`,
            function: 'checkIfCustomerExistsWithEmail',
            category: UICategory.security,
         },
          'Checking if customer exists for email'
        );
        if (!email) {
            throw { code: 'invalid email', text: 'Email is null or empty' };
        }
        const existingCustomers = await MMDirectCustomer.persistorCountByQuery({ email: email.toLowerCase().trim() });
        if (existingCustomers > 0) {
          UILoggerSingleton.getInstance().info({
            module: `Controller`,
            function: 'checkIfCustomerExistsWithEmail',
            category: UICategory.security,
            data: {
                customerCount: existingCustomers
            }
         },
          `Existing customer found`
         );
          return true;
        }
        return false;
      }

    @remote({on: "server"})
    finishResetPassword(): Promise<boolean> {
        return this.login(LoginType.fromToken);
    }

    protected clearPasswords(): void {
        this.newPassword = '';
        this.confirmPassword = '';
        this.password = '';
    }

    @remote({on: "server"})
    loginByType(loginType?: LoginType): Promise<void> {
        if (!this.getPrincipal()) {
            this.resetCustomer();
        }
        switch (Number(loginType)) {
            case LoginType.withNewEmail:
                return this.publicLoginWithNewEmail();

            case LoginType.fromToken:
                return this.publicChangePasswordFromToken();

            default:
                return this.publicLogin(false);
        }
    }

    public async logout(): Promise<void> {
        await this.serverLogout();
        this.clearPasswords();
    }

    @remote({on: "server"})
    postProcessing(loginType: LoginType | void): void {
        if (Number(loginType) === LoginType.withNewEmail) {
            const policy: Policy = this.customer.application.primaryPolicy;
            if (policy.owner) {
                policy.owner.email = this.customer.email;
            }
            if (policy.isSelfInsuredAndSelfOwner() && policy.insured && policy.insured.person) {
                policy.insured.person.email = this.customer.email;
            }
        }
    }

    @remote({on: "server"})
    async fetchAccessories(): Promise<void> {
        LoggerSingleton.getInstance().info({
            module: 'controller',
            function: 'fetchAccessories',
            category: Category.request,
            context: {
                piiLevel: PiiLevelEnum.s2
            },
            data: {
                email: this.customer.email
            },
         },
          'customer login'
        );

        this.amorphic.reqSession.loggingID = this.customer.email;

        try {
            await this.customer.enrichCustomerOptimized();

            await this.customer.fetchQuotes();

            const owner = this.customer.application.primaryPolicy.ownerPerson;

            if (!owner.financials) {
                LoggerSingleton.getInstance().info({
                    module: 'controller',
                    function: 'fetchAccessories',
                    context: {
                        piiLevel: PiiLevelEnum.s2
                    },
                    data: {
                        email: this.customer.email
                    },
                    category: Category.request,
                 },
                  `creating financials for owner object`
                );

                owner.financials = Financials.new(this.customer);
            }

            if (!owner.financials.employerAddress) {
                LoggerSingleton.getInstance().info({
                    module: 'controller',
                    function: 'fetchAccessories',
                    context: {
                        piiLevel: PiiLevelEnum.s2
                    },
                    data: {
                        email: this.customer.email
                    },
                    category: Category.request,
                 },
                  `creating employerAddress for owner object`
                );

                owner.financials.employerAddress = Address.new(this.customer);
            }

            if (!owner.existingPolicies) {
                LoggerSingleton.getInstance().info({
                    module: 'controller',
                    function: 'fetchAccessories',
                    context: {
                        piiLevel: PiiLevelEnum.s2
                    },
                    data: {
                        email: this.customer.email
                    },
                    category: Category.request,
                 },
                  `creating existingPolicies for owner object`
                );

                owner.existingPolicies = ExistingPolicies.new(this.customer);
            }
        } catch(err) {
            const error = {
                isHumanRelated: false,
                data: {
                    email: this.customer.email
                },
                message: err,
            }
            LoggerSingleton.getInstance().info({
                module: 'controller',
                function: 'fetchAccessories',
                category: Category.request,
                context: {
                    piiLevel: PiiLevelEnum.s2
                },
                error: error,
                message: `Error: fetchAccessories`
            });
        }
    }

    /**
     * @remark This is a dummy function just to fulfill Application form contract
     * it was introduced as part of delete case
     * TODO Delete this as part of HL-14757
     */
    @remote({on: "server"})
    checkApplicationAccess(application: MMDirectApplication): Promise<boolean> {
        return Promise.resolve(true);
    }

    /**
     * @remark This is a dummy function just to fulfill Application form contract
     * it was introduced as part of delete case
     * TODO Delete this as part of HL-14757
     */
    @remote({on: "server"})
    prepareForPolicySubmission(applicationNumber: string): Promise<void> {
        return Promise.resolve();
    }

    @remote({on: "server"})
    publicCheckEmailExists(): Promise<boolean> {
        if (!this.email) {
            throw { code: 'invalid email', text: 'email is null or empty' };
        }

        return MMDirectCustomer.persistorFetchByQuery({
                email: this.email
            })
            .then((results) => {
                if (results && results[0] && results[0].passwordHash) {
                    LoggerSingleton.getInstance().info({
                        module: 'Controller',
                        function: 'publicCheckEmailExists',
                        category: Category.request,
                    },
                     `Account already exists`
                    );

                    throw {
                        code: 'account_exists',
                        text: 'An account already exists for this email. Please sign in or use another email address.'
                    };
                }
            });
    }

    @remote({on: 'server'})
    public async serverLogout(): Promise<void> {
        this.setLoggedOutState();
        this.resetCustomer();
    }

    resetCustomer(): void {
        this.customer = MMDirectCustomer.new('mmDirectQuote');
    }

    async finalizeRegistration(): Promise<void> {
        if (typeof window !== 'undefined' && window?.HavenAnalytics) {
            const visitorId = HavenAnalytics.analyticsObject.visitorId;
            const userId: string = await this.publicGetUserId();
    
            // TODO: HL-10080 Revise usage of HavenAnalytics for tracking
            HavenAnalytics.set('dimension', {
                VisitorID: visitorId,
                UserID: userId
            });
    
            HavenAnalytics.set('userid', {
                userId: userId
            });
    
            if(this.customer) {
                CoverpathUtils.dataLayerPush( {
                    'userId': HavenAnalytics.customDimensions.UserID
                });
            }
        }

        this.loggedIn = true;
        this.customer.clientInit();
        this.clearPasswords();
        this.customer.advanceApplicationProgress(12);
    }

    onContentRequest(request: Request, response: Response): void {
        const query = Url.parse(request.url, true).query;

        if(this.accountCenterController.isAccountCenterQueryString(query)){
            this.accountCenterController.onContentRequestHandler(query, request, response);
        }
    }

    @remote({on: "server"})
    public async publicUpdatePolicyAdminStatus(): Promise<boolean> {
        this.policyAdminStatus = await PolicyAdminUtils.getPolicyAdminStatus(this.customer.application.primaryPolicy);
        return true;
    }

    @remote({on: "server"})
    wasPACanceledDeclinedRejected(): boolean {
        if (this.policyAdminStatus) {
            const stage = this.policyAdminStatus.getWorkflowEquivalentStage();
            return stage && /Canceled|Rejected|Declined/.test(stage);
        }
        return false;
    }

    @remote({on: "server"})
    async verifyEmail(): Promise<boolean> {
        const testMode = this.amorphic.config.nconf.get('environment') !== 'production' ? this.amorphic.config.nconf.get('test') || '' : '';
        if (testMode.match(/noverify/) && this.verifyEmailCode === '1234') {
            this.getPrincipal().emailValidated = true;
            return true;
        } else {
            try {
                await this.publicVerifyEmailFromCode();
                return true;
            } catch(err) {

                if (_.get(err, 'code') === AuthenticatedPrincipal.INVALID_VALIDATION_LINK) {
                    LoggerSingleton.getInstance().warn({
                        module: 'controller',
                        function: 'verifyEmail',
                        category: Category.security,
                    },
                     `Failed email verification due to ${AuthenticatedPrincipal.INVALID_VALIDATION_LINK}; user likely entered wrong code`
                    );
                    throw err;
                } else {
                    const error = {
                        isHumanRelated: false,
                        message: JSON.stringify(err)
                    }

                    LoggerSingleton.getInstance().error({
                        module: 'controller',
                        function: 'verifyEmail',
                        category: Category.security,
                        error: error,
                    },
                    `Failed email verification due to ${AuthenticatedPrincipal.INVALID_VALIDATION_LINK}; user likely entered wrong code`
                    );
                    throw err;
                }
            }
        }
    }

    async preServerCall(
        hasChanges: boolean,
        _changes: PreServerCallChanges,
        _context: CallContext,
        forceUpdate: undefined | boolean,
        functionName: string,
        remoteCall: RemoteCall,
        isPublic: boolean,
        HTTPObjs: HTTPObjs
    ): Promise<void> {
        super.preServerCall(hasChanges, _changes, _context, forceUpdate, functionName, remoteCall, isPublic, HTTPObjs);
        await this.setFeatureFlags();

        function isCustomerStale() {
            if (this.loggedIn) {
                return forceUpdate ? true : this.customer.isStale();
            }
            else {
                return false;
            }
        }

        function refreshCustomer(isStale) {
            if (this.customer && isStale) {
                UILoggerSingleton.getInstance().info({
                    module: 'controller',
                    function: 'preServerCall.refreshCustomer',
                    category: UICategory.request,
                },
                 `refreshCustomer`
                );
            }

            return isStale ? refresh.call(this) : true;
        }

        function refresh() {
            return this.customer.refresh()
                .then(this.customer.enrichCustomerOptimized.bind(this.customer));
        }

        const dataForLog = <{status: any,version: number,hasChanges: boolean,customerId: string}>{
            status: this.amorphic.__changeTracking__
        };
        if (this.customer) {
            dataForLog.version = this.customer.__version__;
            dataForLog.hasChanges = hasChanges;
            dataForLog.customerId = this.customer._id;

            this.customer.clearPostServerCallCallbacks();
        }
        UILoggerSingleton.getInstance().info({
            module: 'controller',
            function: 'preServerCall.refresh',
            data: dataForLog,
            category: UICategory.request,
        },
         `refresh`
        );

        this.amorphic.begin();

        return Promise.resolve()
            .then(isCustomerStale.bind(this))
            .then(refreshCustomer.bind(this));
    }

    async postServerCall(hasChanges: boolean, callContext, changeString): Promise<void> {
        // semotus passes in (remoteCall.changes.length > 2, callContext, this.changeString)
        // when changeString exists, we check for changed properties
        // we log out the properties only to avoid exposing PII
        if (changeString) {
            const changedProperties = Object.keys(changeString);
            if (changedProperties.length > 0) {
                UILoggerSingleton.getInstance().info({
                    module: 'Controller',
                    function: 'PostServerCall',
                    context: {
                        piiLevel: UIPiiLevelEnum.s2
                    },
                    data: {
                        properties: changedProperties
                    },
                    category: UICategory.request,
                },
                 'Saving properties to DB'
                );
            }
        }

        if (this.loggedIn && this.customer && this.customer.application && this.customer.application.primaryPolicy) {
            super.elasticPut(this.customer.application.primaryPolicy, false, false);
        }
        if (this.loggedIn) {
            this.customer.cascadeSave(undefined);
        }
        await this.amorphic.end();

        if (this.customer) {
            await this.customer.notifyPostServerCallCallbacks();
        }
    }

    @remote({on: "server"})
    publicCustomerPersistRefresh() {
        return this.customer.persistorRefresh();
    }

    @remote({ on: "server" })
    publicGetUserId(): string {
        return this.customer._id;
    }

    /**
     * Hook called between pre and post server call to validate changes going from
     * client to server.
     * @param changes
     */
    validateServerIncomingObject(changes: Supertype): void {
        this.submittedPolicyValidatingController.validateServerIncomingObject(this.customer, changes);
    }

    /**
     * Checks workflow requirements and changes workflow states
     *
     * @remark This function is duplicated here to fulfill MMDirectControllerInterface contract
     * TODO move this function to a common place as part of HL-14757
     */
    @remote({on: "server"})
    checkRequirement (policy: Policy, requirement: string, futureSubState: string, futureState?: string) {
        let reqToCheck: string;
        try {
            reqToCheck = _.find(policy.currentStageRequirements, req => {
                return req.match(new RegExp(requirement));
            });
            if(!reqToCheck){
                LoggerSingleton.getInstance().info({
                    module: 'accountCenterController',
                    function: 'checkRequirement',
                    category: Category.request,
                },
                 `Requirement: ${requirement} not in currentStageRequirements for policyId: ${policy._id}`
                );
                return;
            }
        }
        catch(err) {
            let message: string;
            // Logging out a specific issue, detailed in production logs in tickets: HL-8317/HL-9445
            if (!policy) {
                message = `Error Policy is missing while attempting to satisfy requirement: ${requirement}`;
            }
            else {
                message = `Error while attempting to satisfy requirement: ${requirement} policyId: ${policy._id}`;
            }

            const error = {
                isHumanRelated: false,
                message: err
            }
            LoggerSingleton.getInstance().error({
                module: 'accountCenterController',
                function: 'checkRequirement',
                category: Category.request,
                error: error,
            },
             message
            );
            return;
        }

        let force = false;
        switch(reqToCheck) {
            case 'Accept':
                LoggerSingleton.getInstance().warn({
                    module: 'accountCenterController',
                    function: 'checkRequirement',
                    category: Category.request,
                },
                 'Cannot \'Accept\' Policy'
                );
                return;

            case 'Resend Application to Docusign':
                force = true;
                break;
        }
        return policy.fulfillReq(reqToCheck, futureSubState, futureState, force);
    }

    public isApplicationSubmitted(): boolean {
        return !!_.get(this, 'customer.application.primaryPolicy.submittedAt', false);
    }

    @remote({on: 'server'})
    public async updatePolicyInfoInPolicyAdmin(path: string, body): Promise<void> {
        const policy = this.customer.application.primaryPolicy;
        const policyAdminStatus = await PolicyAdminUtils.getPolicyAdminStatus(policy);
        return PolicyAdminUtils.updatePolicyInfoInPolicyAdmin(path, policy, body, policyAdminStatus.getPolicyAdminPolicyId());
    }

    @remote({on: 'server'})
    public getPaymentInfo() {
        return PolicyAdminClient.getPaymentInfo(this.customer.application.primaryPolicy);
    }

    @remote({on: 'server'})
    public getBeneficiaryInfo() {
        return PolicyAdminClient.getBeneficiaryInfo(this.customer.application.primaryPolicy);
    }

    @remote({on: 'server'})
    public setLifeBridge() {
        this.isLifeBridge = true;
    }

    /**
     * makes sure LifeBridge is properly initialized
     */
    async initializeLifeBridge(): Promise<void> {
        this.fillOutLifeBridgeQuestions();
        await this.publicSave();
    }

    /**
     * LifeBridge applications have certain questions filled out and locked
     */
    fillOutLifeBridgeQuestions(): void {
        const lifeBridgeOwnerEmail = this.amorphic.config.nconf.get('lifeBridgeOwnerEmail');
        const lifeBridgeAuthorizedSignerEmail = this.amorphic.config.nconf.get('lifeBridgeOwnerEmail');

        const policy = <MMDirectPolicy> this.customer.application.primaryPolicy;
        policy.insured.address.state = 'CT';
        policy.ownerClassification = 'trust';
        policy.ownerClassificationTrigger();

        const insured = policy.insured;
        const financials = insured.financials;
        const bene = policy.primaryBeneficiary[0];
        const owner = policy.trustOrOtherEntity;

        owner.type = 'trust';
        owner.trustName = 'LifeBridge Free Life Insurance Program Trust Agreement';
        owner.email = lifeBridgeOwnerEmail;
        owner.trustDate = new Date('04/01/2003');
        owner.trustType = 'Irrevocable';

        const authorizedTrustee = Trustee.new(this.customer);
        authorizedTrustee.type = 'Entity';
        authorizedTrustee.entityName = 'The MassMutual Trust Company, FSB';
        owner.authorizedTrusteeList = [authorizedTrustee];
        owner.authorizedTrusteeList[0].entityAuthorizedSigner = Signer.new();
        const authorizedSigner = owner.authorizedTrusteeList[0].entityAuthorizedSigner;
        authorizedSigner.firstName = 'Elithea';
        authorizedSigner.lastName = 'Mas';
        authorizedSigner.title = 'Head of Trust Fiduciary Services';
        authorizedSigner.email = lifeBridgeAuthorizedSignerEmail;
        authorizedSigner.residentialAddress.street = '100 Bright Meadow Boulevard';
        authorizedSigner.residentialAddress.city = 'Enfield';
        authorizedSigner.residentialAddress.zip = '06082';
        authorizedSigner.residentialAddress.state = 'CT';
        authorizedSigner.dob = new Date('01/01/1980');
        authorizedSigner.SSN = '000-00-0000';
        authorizedSigner.phone.number = '800-272-2216';
        authorizedSigner.phone.type = '2';
        authorizedSigner.gender = '2';

        const authorizedGrantor = Grantor.new(this.customer);
        authorizedGrantor.type = 'entity';
        authorizedGrantor.entityName = 'Massachusetts Mutual Life Insurance Company';
        authorizedGrantor.taxId = '04-1590850';
        authorizedGrantor.taxIdTrigger();
        owner.trustGrantorList = [authorizedGrantor];

        owner.relationshipAsBeneficiary = 'other';
        owner.relationshipAsBeneficiaryDetails = 'N/A';
        owner.relationshipAsGrantor = 'other';
        owner.relationshipAsGrantorDetails = 'N/A';
        owner.doesTrustAuthorizeTrustee = 'Yes';
        owner.certifiedDocumentSignee = 'designated';
        owner.designatedTrusteeIndex = 0;
        owner.designatedTrusteeIndexTrigger();

        const legalAddress = owner.legalAddress;
        const ownerAddress = owner.ownerAddress;
        legalAddress.street = '100 Bright Meadow Boulevard';
        legalAddress.city = 'Enfield';
        legalAddress.zip = '06082';
        legalAddress.state = 'CT';
        owner.trustSitus = 'CT';
        owner.isAddressSameAsInsured = 'No';
        ownerAddress.street = '100 Bright Meadow Boulevard';
        ownerAddress.city = 'Enfield';
        ownerAddress.zip = '06082';
        ownerAddress.state = 'CT';
        owner.isMailingAddressSameAsLegal = 'Yes';

        owner.taxId = '56-6610867';
        owner.isUSEntity = true;

        policy.ownerDelegate = 'ownerEstate';

        policy.product = Product.products.ET10.code;
        policy.productType = Product.productTypes.Term.code;
        policy.term = 10;
        policy.faceAmount = 50000;
        policy.waiver = false;
        policy.waiverTrigger();
        policy.paymentFrequency = 'Annual';
        policy.isOwnerAlsoPayer = 'true';
        policy.ownerType = 'trust';
        policy.hasSecondaryAddressee = false;

        policy.selfInsured = 'true';
        policy.insuredType = 'you';
        policy.insuredTypeTrigger();

        financials.isNotFATCAWitholding = true;
        financials.isFATCAResidencyStatus = true;

        insured.existingPolicies.replaceInforceApplication = false;

        // set beneficiaryType to "Other Trust"
        policy.beneficiaryType = '7';

        // LifeBridge has no contingent beneficiary
        policy.hasContingentBeneficiary = false;
        policy.hasContingentBeneficiaryTrigger();

        if (!bene.entity) {
            bene.entity = Entity.new(this.customer);
            bene.entity.legalAddress = Address.new();
        }
        bene.entity.name = 'LifeBridge Free Life Insurance Program Trust Agreement, The MassMutual Trust Company, FSB, Trustee';
        bene.entity.legalAddress.street = '100 Bright Meadow Boulevard';
        bene.entity.legalAddress.line1 = '';
        bene.entity.legalAddress.zip = '06082';
        bene.entity.legalAddress.city = 'Enfield';
        bene.entity.legalAddress.state = 'CT';
        bene.entity.TIN = '566-61-0867';
        bene.entity.noTIN = false;
        bene.entity.trustType = 'irrevocable';

        policy.isDOL = false;
        policy.collaterallyAssigned = false;
        policy.premiumSource = true;
        policy.economicIncentive = false;
        policy.beneficialInterest = false;

        // billing info is handled by Policy Admin
        policy.paymentInApplication = 'now';
        policy.paymentAccountType = '1';
        policy.paymentBankName = 'Bank';
        policy.paymentABA = '000000000';
        policy.paymentAccount = '000';

        policy.insuredType = 'you';
        policy.insuredTypeTrigger();

        policy.premiumSourceYesNoOther = 'other';

        policy.reasonForInsurance = "P";
        policy.personalReasonForInsurance = "education";

        policy.owner = policy.trustOrOtherEntity;
        policy.owner.isTINCorrect = true;
        policy.owner.financials.isNotFATCAWitholding = true;
        policy.owner.financials.isFATCAResidencyStatus = true;

        this.customer.channel = Product.channels.LifeBridge.code;
        this.customer.emailValidated = true;
    }
    @remote({on: 'server'})
    public getContactInfo() {
        return PolicyAdminClient.getContactInfo(this.customer.application.primaryPolicy);
    }

    public async lookUpIsAuth0Enabled(): Promise<boolean> {
        return Auth0Utils.isAuth0EnabledByProvider(AppConfigTypes.AUTH0_ENABLED_MMD);
    }

    protected inNonProductionMode(): boolean {
        return false;
    }

    protected async postProcessEmailChange(): Promise<void> {
        // NoOp
    }

    protected async returnInformationToClient(identifiedPrincipal: IdentifiedPrincipal): Promise<AuthenticationInformation> {
        const authInformation = {
            customerId: this.customer._id,
            isExistingUser: identifiedPrincipal.isExistingUser
        };

        return authInformation;
    }

    /**
     * MMD specific calls after creating a new customer
     */
    protected async runNewAccountActions(): Promise<void> {
        const newCustomerProps = this.makePropsForNewCustomer();

        // Quick hack to fix HL-11075.
        // TODO to remove this HL-12875
        // Make a quick copy of paid source parameters
        const paidSourceProps = {
            paidSource: this.customer.paidSource || '',
            paidSource_cid: this.customer.paidSource_cid || '',
            paidSource_pdata: this.customer.paidSource_pdata || '',
            paidSource_campaign: this.customer.paidSource_campaign || ''
        };

        this.customer.initializeQuote(newCustomerProps.quoteType);
        this.customer.reset();
        this.customer.updateProps(newCustomerProps);

        this.customer.paidSource = paidSourceProps.paidSource;
        this.customer.paidSource_cid = paidSourceProps.paidSource_cid;
        this.customer.paidSource_pdata = paidSourceProps.paidSource_pdata;
        this.customer.paidSource_campaign = paidSourceProps.paidSource_campaign;

        await this.customer.createPolicy(this.customer.mmdQuote.product);
        this.customer.application.primaryPolicy.ownerPersonResidentialAddress.state = newCustomerProps.address.state;

        if (this.isLifeBridge){
            await this.initializeLifeBridge();

            UILoggerSingleton.getInstance().info({
                module: 'Controller',
                function: 'runNewAccountActions',
                category: UICategory.request,
            },
             'Initialized LifeBridge'
            );
        }

        // Do not wait for finalizeRegistration
        this.finalizeRegistration();

        await this.customer.persistSave();
    }

    protected async fetchData(): Promise<boolean> {
        await this.fetchAccessories();

        return true;
    }

    @remote({ on: 'server' })
    public async checkIfAccountVerified(): Promise<boolean> {
      let isVerified = false;

      if (this.isAccountVerified) {
        isVerified = true;
      } else {
        if (this.isAuth0Enabled) {
          const accountInfo: AMCAuth0ResponseUser = await this.getAMCController().getAMCUserInfo(this.email);
          if (accountInfo) {
            isVerified = accountInfo.email_verified;
          }
        }
      }

      return isVerified;
    }

    @remote({ on: 'server' })
    public async resolveAuth0Data(): Promise<void> {
      await this.setAuth0Configuration();

      if (this.isAuth0Enabled) {
        await this.getAMCController().getAMCUserInfo();
      }

      await this.resolveAccountVerificationStatus();
    }

    protected async updateAuthOwner(authOwner: AuthOwner): Promise<void> {
        // NoOp
    }

    protected async updateIdentity(principal: PrincipalCustomer): Promise<void> {
        const identityService = this.getIdentityService();
        let identity: Identity = await identityService.loadIdentityByIdentifier(this.auth0userId);

        if (this.isAuth0Enabled) {
            if (!identity) {
                identity = await identityService.createNewIdentity(this.auth0userId, true);
                await identityService.associateApplicationWithIdentity(this.customer, identity);
            } else {
                const authorizedAccount: AccountAuthorization = identity.account.accountAuthorizations.find(accountAuth => {
                    return accountAuth.managedEntityType.templateName === MMDirectCustomer.amorphicClassName;
                });
                if (!authorizedAccount) {
                    await identityService.associateApplicationWithIdentity(this.customer, identity);
                }
            }
            this.authenticatedIdentity = identity;
        }
    }

    public getAuth0Configurations(): Auth0Configs {
        return Auth0Utils.getAuth0Configuration(Auth0Channel.MMD);
    }

    public getAuth0Secret(): string {
        return Auth0Utils.getAuth0Secret(Auth0Channel.MMD);
    }

    public async exchangeAuth0CodeForToken(code: string, redirectUri: string): Promise<Auth0Response> {
        return Auth0Client.exchangeCodeForToken(code, redirectUri, Auth0Channel.MMD);
    }

    async setFeatureFlags() {
        const startTime = process.hrtime();

        //we want to leverage the CP flags... MMD does not need a separately managed ste
        const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
        if (ldInstance) {
            this.artEnabled = await ldInstance.getFeatureFlagValueAsBoolean(
                FeatureFlags.artEnabled,
                'mm-direct'
            );
            this.earlyInsuredConsentEnabled = await ldInstance.getFeatureFlagValueAsBoolean(
                FeatureFlags.earlyInsuredConsentEnabled,
                'mm-direct'
            );
            this.mmGiactWebDebitVerificationServiceEnabled = await ldInstance.getFeatureFlagValueAsJSON(
                FeatureFlags.mmClientGiactWebDebitVerificationServiceEnabled,
                'mm-direct',
                null
            );

            this.enableGenesysCobrowse = await ldInstance.getFeatureFlagValueAsBoolean(
                FeatureFlags.enableGenesysCobrowse,
                'mm-direct'
            );

            const diffCycleTime = process.hrtime(startTime);
            const totalMS = diffCycleTime[0] * 1000 + diffCycleTime[1] / 1000000;
            UILoggerSingleton.getInstance().info(
                {
                    module: 'controller',
                    function: 'setFeatureFlag',
                    category: UICategory.request,
                },
                 `Time taken ${totalMS} ms`
            );
        }
    }

    @remote({ on: 'server' })
    public async resolveHeavyUserFeature(): Promise<boolean> {
      this.isHeavyUserEnabled = await Auth0Utils.isAuth0EnabledByProvider(AppConfigTypes.HEAVY_USER_ENABLED);
      return this.isHeavyUserEnabled;
    }

    @remote({ on: 'server' })
    public async getHeavyUserAMCdata(): Promise<void> {
        if (this.isHeavyUserEnabled) {
            LoggerSingleton.getInstance().info(
                {
                    module: 'AMCController',
                    function: 'getHeavyUserAMCdata',
                    category: Category.request,
                },
                 'Going to get amc heavy user data'
            );

            await this.getAMCController().getHeavyUserAMCdata();

            if (this.getAMCController().AMCHeavyUserData) {
                await this.setAMCuserInfoForCustomer();
            }
        }
    }

    @remote({ on: 'server' })
    private async handleChangesForHeavyUserProps(): Promise<void> {
        const insuredSSNwasAlreadySet =
            CustomerUtils.formatSSN(this.customer.application.primaryPolicy.insured.person.SSN) ===
            CustomerUtils.formatSSN(this.getAMCController().AMCHeavyUserData.governmentId);
        const ownerSSNwasAlreadySet =
            CustomerUtils.formatSSN(this.customer.application.primaryPolicy.ownerPerson.SSN) ===
            CustomerUtils.formatSSN(this.getAMCController().AMCHeavyUserData.governmentId);

        const setInsuredHeavyUserInfo =
            this.customer.application.primaryPolicy.isInsuredInfoSameAsOwners() && !insuredSSNwasAlreadySet;
        const clearInsuredHeavyUserInfo =
            !this.customer.application.primaryPolicy.isInsuredInfoSameAsOwners() && insuredSSNwasAlreadySet;

        if (setInsuredHeavyUserInfo) {
            await this.setInsuredHeavyUserInfo();
        } else if (clearInsuredHeavyUserInfo) {
        // clear insured pii
            await this.clearInsuredHeavyUserInfo();
        } else if (!ownerSSNwasAlreadySet) {
            await this.setOwnerHeavyUserInfo();
        } else if (!this.customer.application.primaryPolicy.ownerPerson.email) {
            await this.resetOwnerEmail();
        }
    }

    private async setInsuredHeavyUserInfo(): Promise<void> {
        this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.insured.person, true);
        // necessary? heartbeat?
        this.customer.persistSave();
    }

    private async setOwnerHeavyUserInfo(): Promise<void> {
        this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.ownerPerson, true);
        // necessary? heartbeat?
        this.customer.persistSave();
    }

    private async clearInsuredHeavyUserInfo(): Promise<void> {
        this.clearPersonsHeavyUserData(this.customer.application.primaryPolicy.insured.person);

        // necessary?
        this.customer.persistSave();
    }

    private async resetOwnerEmail(): Promise<void> {
        this.customer.application.primaryPolicy.ownerPerson.email = this.customer.email;

        this.customer.persistSave();
    }

    private async setAMCuserInfoForCustomer(): Promise<void> {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'setAMCuserInfoForCustomer',
            category: UICategory.security,
          },
           'we want to set the amc pii to the customer object now'
        );

        this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.ownerPerson, true);
        // in case name is not set then update it for the owner
        if (!this.customer.firstName) {
          this.setPersonsDataToHeavyUserData(this.customer);
        }
        if (this.customer.application.primaryPolicy.isInsuredInfoSameAsOwners()) {
          this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.insured.person, true);
        }

        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'setAMCuserInfoForCustomer',
            category: UICategory.security,
          },
           'we set the info and it should show up as readonly for client'
        );

        // necessary?
        await this.customer.persistSave();
    }

    private setPersonsDataToHeavyUserData(person: Person | Customer, setAllData?: boolean): void {
        person.firstName = this.getAMCController().AMCHeavyUserData.firstName;
        person.lastName = this.getAMCController().AMCHeavyUserData.lastName;
        if (person instanceof Person) {
            person.middleName = this.getAMCController().AMCHeavyUserData.middleName;
          // dont need to set the PII every time
            if (setAllData) {
                person.dob = new Date(this.getAMCController().AMCHeavyUserData.birthDate);
                person.SSN = CustomerUtils.formatSSN(this.getAMCController().AMCHeavyUserData.governmentId);
            }
        }
    }

    private clearPersonsHeavyUserData(person: Person): void {
        person.dob = null;
        person.SSN = null;
    }

  @remote({ on: 'server' })
  public async insuredAcknowledgementForAutomation(): Promise<any> {
    const ret = { loaded: true, data: false };
    if (!Utils.isProdEnv()) {
      const policy = this.customer.application.primaryPolicy;
      const cancelled = policy ? policy.wasCanceledDeclined() : true;
      if (cancelled) {
        ret.data = false;
      } else {
        await policy.fulfillRequirements(['IC1R4 Insured consent has been received']);
        ret.data = true;
      }
    }
    return ret;
  }

  @remote({ on: 'server' })
  public async insuredAcknowledgement(token: string): Promise<any> {
    const secret: string = this.amorphic.config.nconf.get('insuredAcknowledgeKey');
    let data;
    const ret = { loaded: true, data: false };
    try {
      data = jwt.verify(token, secret);
    } catch (err) {
      ret.data = false;
    }
    if (data) {
      const policies = await this.getPolicy(data.polno);
      const policy = policies[0];
      const cancelled = policy ? policy.wasCanceledDeclined() : true;
      if (cancelled) {
        ret.data = false;
      } else {
        await policy.fulfillRequirements(['IC1R4 Insured consent has been received']);
        ret.data = true;
      }
    }
    return ret;
  }

  @remote({ on: 'server' })
  async getPolicy(policyNumber: string) {
    return Policy.getFromPersistWithQuery({ policyNumber });
  }

  @remote({ on: 'server' })
  public async setPiiByProperty(prop: string, attrValue: string): Promise<string> {
    throw new Error('Not implemented'); //TODO https://massmutual.atlassian.net/browse/HL-46584
  }

  @remote({ on: 'server' })
  async setPiiByPersonId(personId: string, attrName:string, attrValue: string): Promise<string> {
    return attrValue; //TODO https://massmutual.atlassian.net/browse/HL-46584
  }

  @remote({ on: 'server' })
  public async getPiiByPersonId(personId: string, attrName: string, lastDigits: number = 0): Promise<string> {
     throw new Error('Not implemented'); //TODO https://massmutual.atlassian.net/browse/HL-46584
  }

  public async getPiiValueByKey(attrKey: string): Promise<string> {
    return attrKey; //TODO https://massmutual.atlassian.net/browse/HL-46584
  }

  @remote({ on: 'server' })
  public async handlePiiAfterAutoFill(): Promise<void> {
    return; //TODO https://massmutual.atlassian.net/browse/HL-46584
  }

  @serverOnly()
  public async handlePiiAfterApplicationClone(): Promise<void> {
    return; //TODO https://massmutual.atlassian.net/browse/HL-46584
  }
}
